import {
  type OfflineDataStoreIdentity,
  type OfflineDataStoreScope,
  parseDatabaseName,
} from './offline-data-store';

export type OfflineLocationScopeUnavailableReason =
  | 'missing'
  | 'ambiguous'
  | 'unreadable';

export type OfflineLocationScopeDiscovery =
  | { status: 'found'; scope: OfflineDataStoreScope }
  | { status: 'unavailable'; reason: OfflineLocationScopeUnavailableReason };

/**
 * Finds the location database of a user from the database names in the browser, so the location
 * does not have to be known first. The MVP supports one location per user. No database is
 * `missing`, and more than one is `ambiguous`. A browser that cannot list databases is `unreadable`.
 */
export const discoverOfflineLocationScope = async (
  user: OfflineDataStoreIdentity,
  indexedDbFactory: IDBFactory
): Promise<OfflineLocationScopeDiscovery> => {
  let databases: IDBDatabaseInfo[];
  try {
    databases = await indexedDbFactory.databases();
  } catch {
    // Older browsers have no `databases()`, and it rejects when the browser cannot list.
    return { status: 'unavailable', reason: 'unreadable' };
  }

  const scopes = databases.flatMap(({ name }) => {
    const scope = name && parseDatabaseName(name);
    return scope &&
      scope.cluster === user.cluster &&
      scope.project === user.project &&
      scope.userId === user.userId
      ? [scope]
      : [];
  });

  const [scope, ...others] = scopes;
  if (!scope) return { status: 'unavailable', reason: 'missing' };
  if (others.length > 0) return { status: 'unavailable', reason: 'ambiguous' };
  return { status: 'found', scope };
};
